import { useCallback, useContext, useEffect, useMemo } from 'react';
import get from 'lodash.get';
import set from 'lodash.set';
import cloneDeep from 'lodash.clonedeep';

import type { FieldValue } from '../types/FieldValue';
import { ContestManageContext } from '../context/ContestManageContext';

export const useFieldValue = <V extends FieldValue>(
  sectionKey: string,
  path: string[],
  defaultValue?: V,
  isVisible?: boolean
) => {
  const { values, setValues, errors, touched, setTouched } = useContext(ContestManageContext);

  const handleSetValue = useCallback(
    (newValueOrCb: V | ((prevValue: V) => V)) => {
      setValues((prev) => {
        const prevValue = get(prev, [sectionKey, ...path]) as V;
        const newValue =
          typeof newValueOrCb === 'function' ? newValueOrCb(prevValue) : newValueOrCb;

        return set(cloneDeep(prev), [sectionKey, ...path], newValue);
      });
    },
    [path, sectionKey, setValues]
  );

  const fieldValue = useMemo(() => get(values, [sectionKey, ...path]), [path, sectionKey, values]);

  const fieldErrors = useMemo(() => {
    const e = get(errors, [sectionKey, ...path]) as string[] | undefined;
    return !e || Array.isArray(e) ? e : undefined;
  }, [path, sectionKey, errors]);

  const fieldIsTouched = useMemo(
    () => get(touched, [sectionKey, ...path]) as boolean | undefined,
    [path, sectionKey, touched]
  );

  const onValueChange = useCallback(
    (newValueOrCb: V | ((prevValue: V) => V), skipTouched?: boolean) => {
      handleSetValue(newValueOrCb);
      if (!skipTouched) {
        setTouched((prev) => set(prev, [sectionKey, ...path], true));
      }
    },
    [handleSetValue, path, sectionKey, setTouched]
  );

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    if (typeof defaultValue === 'undefined') {
      return;
    }
    if (typeof fieldValue !== 'undefined') {
      return;
    }
    if (fieldIsTouched) {
      return;
    }

    onValueChange(defaultValue, true);
  }, [defaultValue, fieldIsTouched, fieldValue, isVisible, onValueChange]);

  return {
    value: fieldValue,
    onValueChange,
    errors: fieldErrors,
    isTouched: fieldIsTouched,
  };
};
